import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Input from '@/components/basicComponents/input';
import { useErrorMessages } from '@/hooks/i18n';
import { useMounted } from '@/hooks/use-mounted';
import { themeVar } from '@/styles/theme';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';

interface EnterCodeProps {
  isOpen: boolean;
  verify2FACode: (verificationCode: string) => Promise<void>;
  onClose: () => void;
}

const EnterCode = ({ isOpen, verify2FACode, onClose }: EnterCodeProps) => {
  const isMounted = useMounted();
  const { default: defaultErrorMessage } = useErrorMessages();
  const { control, setError, handleSubmit } = useForm<{ verificationCode: string }>({ defaultValues: { verificationCode: '' } });

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const onSubmit = async (data: { verificationCode: string }) => {
    try {
      setErrorMessage(null);
      setLoading(true);

      const { verificationCode } = data;
      await verify2FACode(verificationCode);
      onClose();
    } catch (error: any) {
      if (error.code == 'auth/invalid-verification-code') {
        if (isMounted()) {
          setError('verificationCode', { message: 'Invalid verification code. Please try again.' });
          return;
        }
        return;
      }

      setErrorMessage(defaultErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      title="SMS code verification"
      confirmButton={{
        name: 'Confirm',
        onClick: handleSubmit(onSubmit),
        disabled: loading
      }}
      showCloseIcon={!loading}
      showCancelButton={!loading}
      isUseOutsideClick={!loading}
    >
      {loading ? (
        <ModalLoader />
      ) : (
        <>
          {errorMessage && (
            <ErrorBannerWrapper>
              <Banner
                icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                title="Error"
                description={errorMessage}
                bgColor={themeVar('context.error')}
              />
            </ErrorBannerWrapper>
          )}
          <Controller
            name="verificationCode"
            control={control}
            rules={{
              required: 'Verification code is required.',
              pattern: {
                value: /^\d{6}$/,
                message: 'Verification code must be a 6-digit number.'
              }
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Input
                label="Enter Verification Code"
                value={value || ''}
                onChange={(e) => {
                  onChange(e.target.value);
                }}
                errorText={error?.message}
                type="text"
                description="Enter your SMS code"
                size="md"
                isRequiredField
                autoFocus
              />
            )}
          />
        </>
      )}
    </ModalWrappedContent>
  );
};

export default EnterCode;

const ErrorBannerWrapper = styled.div`
  margin-bottom: 15px;
`;
